import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Handshake } from "lucide-react";
import PageHero from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import PhotoGallery from "@/components/PhotoGallery";
import { useSeo, JsonLd } from "@/lib/seo";
import { publicCatalogApi } from "@/lib/api";

const DURATIONS = [3, 4, 5];
const PHOTOS = [9, 3, 7, 11, 10].map((n) => `/assets/entreprises/entreprise-${String(n).padStart(2, "0")}.jpg`);
const SHORT = { Light: "DJ + Lumière", Sound: "DJ + Son", Full: "DJ + Son & Lumière" };

const fmt = (v) => `${Math.round(v).toLocaleString("fr-FR")} €`;
const slug = (name) => name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const shortLabel = (p) => SHORT[(p.name.match(/DJ Set (\w+)/) || [])[1]] || p.description || p.name;
const splitFeatures = (p) => {
  const all = p.features || [];
  const ideal = all.find((f) => /^idéal pour/i.test(f));
  return { included: all.filter((f) => f !== ideal), ideal: ideal?.replace(/^idéal pour\s*:\s*/i, "") };
};

function PackageCard({ p }) {
  const { included, ideal } = splitFeatures(p);
  const s = slug(p.name);
  return (
    <div data-testid={`bar-pack-${s}`} className={`card-luxe p-7 md:p-8 reveal relative flex flex-col ${p.is_featured ? "border-[#DCB457]/60" : ""}`}>
      {p.is_featured && (
        <span data-testid={`bar-pack-${s}-badge`} className="absolute -top-3 left-6 bg-[#DCB457] text-black text-[10px] uppercase tracking-[0.22em] font-medium px-3 py-1">Le plus choisi</span>
      )}
      <h3 className="font-serif-luxe text-[22px] md:text-[26px] leading-[1.12]">{p.name.split(" — ")[0]}</h3>
      {p.description && <p className="text-[11px] uppercase tracking-[0.22em] text-[#DCB457] mt-2">{p.description}</p>}
      <div className="mt-5 border-t border-[#DCB457]/20 pt-5 flex items-baseline gap-2 flex-wrap">
        <span className="font-serif-luxe text-3xl md:text-4xl gold-text whitespace-nowrap" data-testid={`bar-pack-${s}-price`}>{fmt(p.price_ttc)} TTC</span>
        <span className="text-[11px] uppercase tracking-[0.18em] text-white/55">/ heure</span>
      </div>
      <p className="text-[11px] uppercase tracking-[0.18em] text-white/45 mt-2" data-testid={`bar-pack-${s}-min`}>Minimum de réservation : {p.min_quantity || 3} heures</p>
      {p.subtitle && <p className="text-white/60 text-[13px] mt-4 leading-relaxed">{p.subtitle}</p>}
      <ul className="mt-6 space-y-2.5 flex-1">
        {included.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-white/75 text-[13px] leading-snug">
            <Check size={14} strokeWidth={1.5} className="text-[#DCB457] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {ideal && (
        <p className="mt-6 pt-4 border-t border-[#DCB457]/15 text-white/55 text-[12.5px] leading-relaxed">
          <span className="text-[#DCB457] uppercase tracking-[0.18em] text-[10px] block mb-1">Idéal pour</span>{ideal}
        </p>
      )}
      <Link to="/contact" data-testid={`bar-pack-${s}-cta`} className="btn-gold mt-7 w-full !py-3 !text-[11px]">Demander ce DJ Set</Link>
    </div>
  );
}

function PricingTable({ packages }) {
  return (
    <div className="card-luxe !p-0 overflow-x-auto reveal mt-16" data-testid="bar-pricing-table">
      <table className="w-full min-w-[560px] text-left">
        <thead>
          <tr className="border-b border-[#DCB457]/25">
            <th className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5">Durée</th>
            {packages.map((p) => (
              <th key={p.id} className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5 text-right">{shortLabel(p)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DURATIONS.map((h) => (
            <tr key={h} className="border-b border-white/5 hover:bg-white/[0.02] transition" data-testid={`bar-pricing-row-${h}h`}>
              <td className="px-5 md:px-8 py-5 text-white/80 text-sm">{h} heures</td>
              {packages.map((p) => (
                <td key={p.id} className="px-5 md:px-8 py-5 text-right gold-text font-serif-luxe text-xl" data-testid={`bar-pricing-${slug(p.name)}-${h}h`}>{fmt(p.price_ttc * h)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PartnerBlock() {
  return (
    <div className="mt-16 reveal max-w-3xl mx-auto card-luxe p-7 md:p-10 text-center" data-testid="bar-partner-block">
      <Handshake size={26} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
      <h3 className="font-serif-luxe text-2xl md:text-3xl">Vous organisez régulièrement des soirées ?</h3>
      <p className="text-white/65 text-sm md:text-base leading-relaxed mt-4">
        Une tarification partenaire peut être proposée aux bars, restaurants et établissements organisant plusieurs événements dans l'année.
      </p>
      <p className="text-white/65 text-sm md:text-base leading-relaxed mt-3">
        Programmation hebdomadaire, mensuelle ou saisonnière : contactez My Animation pour obtenir une proposition adaptée à votre fréquence d'événements.
      </p>
      <Link to="/contact" data-testid="bar-partner-cta" className="btn-ghost-gold mt-7 inline-flex">Demander un tarif partenaire</Link>
    </div>
  );
}

export default function BarRestaurant() {
  const [packages, setPackages] = useState(null);

  useSeo({
    title: "DJ Bar & Restaurant Lille | DJ Set professionnel — My Animation",
    description: "DJ professionnel pour bars et restaurants à Lille et dans les Hauts-de-France. DJ Set avec sonorisation, éclairage ou formule complète dès 119 €/h.",
    path: "/dj-bar-restaurant-lille",
    keywords: "DJ bar Lille, DJ restaurant Lille, DJ afterwork Lille, DJ terrasse, DJ guinguette, DJ bar Hauts-de-France, DJ bar Belgique",
  });

  useEffect(() => {
    publicCatalogApi.byCategory("bar-restaurant", false)
      .then((d) => setPackages(d.packages || []))
      .catch(() => setPackages([]));
  }, []);

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "DJ Set Bars & Restaurants",
    serviceType: "DJ pour bar et restaurant",
    provider: { "@type": "LocalBusiness", name: "My Animation", telephone: "+33769224956" },
    areaServed: ["Lille", "Nord", "Hauts-de-France", "Belgique"],
    description: "DJ Set sur mesure pour bars, restaurants, terrasses, guinguettes, afterworks et soirées à thème.",
    offers: (packages || []).map((p) => ({
      "@type": "Offer",
      name: p.name,
      priceCurrency: "EUR",
      priceSpecification: { "@type": "UnitPriceSpecification", price: p.price_ttc, priceCurrency: "EUR", unitText: "heure" },
    })),
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="DJ Set — Bars & Restaurants"
        title={<>Le son de votre <span className="italic gold-text">établissement,</span><br />le temps d'une soirée.</>}
        subtitle="Afterwork, soirée festive, ambiance lounge, terrasse, soirée à thème ou événement spécial : My Animation crée un DJ Set sur mesure, adapté à votre clientèle, à l'identité de votre établissement et à l'ambiance recherchée."
        img="/assets/entreprises/entreprise-09.jpg"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-b border-[#DCB457]/15" data-testid="bar-forfaits-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal text-center">
            <span className="eyebrow">Nos DJ Sets</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
              Trois formules, <span className="italic gold-text">tarif à l'heure.</span>
            </h2>
            <p className="text-white/60 text-sm md:text-base mt-5 max-w-2xl mx-auto">
              DJ bar à Lille, DJ restaurant dans le Nord, terrasse ou guinguette en Hauts-de-France et en Belgique : choisissez la formule adaptée à l'équipement de votre établissement.
            </p>
          </div>

          {packages === null ? (
            <p className="text-white/45 text-sm italic text-center">Chargement des formules…</p>
          ) : packages.length === 0 ? (
            <p className="text-white/45 text-sm italic text-center">Nos formules arrivent bientôt.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {packages.map((p) => <PackageCard key={p.id} p={p} />)}
              </div>
              <PricingTable packages={packages} />
            </>
          )}

          <div className="text-center mt-8 space-y-2" data-testid="bar-pricing-note">
            <p className="text-white/70 text-xs uppercase tracking-[0.18em]">Tarifs TTC — TVA non applicable, article 293 B du CGI.</p>
            <p className="text-white/50 text-sm">Déplacement inclus selon secteur. Au-delà, des frais éventuels peuvent être précisés sur le devis.</p>
          </div>

          <PartnerBlock />
        </div>
      </section>

      <PhotoGallery
        images={PHOTOS}
        eyebrow="Ambiances"
        title={<>Des soirées qui <span className="italic gold-text">font vibrer.</span></>}
        subtitle="DJ sets, jeux de lumière et ambiances festives animés par My Animation."
        testIdPrefix="bar-gallery"
      />

      <CTAFooter />
    </>
  );
}
